import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import * as amplitude from "@amplitude/analytics-browser";

export type AmplitudeClientLike = {
  track: (
    eventName: string,
    properties?: Record<string, unknown>,
  ) => { promise?: Promise<unknown> };
  flush: () => { promise?: Promise<unknown> };
  getDeviceId: () => string | undefined;
  /**
   * Associates subsequent Amplitude events with the given user id (e.g. the
   * signed-in Cognito user's `userId`), or clears the association when
   * called with `undefined` (e.g. on sign-out).
   */
  setUserId: (userId: string | undefined) => void;
};

interface AmplitudeContextValue {
  client: AmplitudeClientLike | null;
}

const AmplitudeContext = createContext<AmplitudeContextValue>({ client: null });

export function useAmplitude(): AmplitudeClientLike | null {
  return useContext(AmplitudeContext).client;
}

interface AmplitudeProviderProps {
  children: ReactNode;
  apiKey: string;
  deviceId?: string;
}

/**
 * Initializes Amplitude and provides the client to the React tree.
 */
export function AmplitudeProvider({
  children,
  apiKey,
  deviceId,
}: AmplitudeProviderProps) {
  const [client, setClient] = useState<AmplitudeClientLike | null>(null);

  useEffect(() => {
    amplitude.init(apiKey, {
      deviceId,
      defaultTracking: true,
    });
    setClient({
      track: (eventName, properties) => amplitude.track(eventName, properties),
      flush: () => amplitude.flush(),
      getDeviceId: () => amplitude.getDeviceId(),
      setUserId: (userId) => amplitude.setUserId(userId),
    });
  }, [apiKey, deviceId]);

  return (
    <AmplitudeContext.Provider value={{ client }}>
      {children}
    </AmplitudeContext.Provider>
  );
}
